import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'

// "Remember to close your flight plan", as mik-ng's entry wizard has it:
// pilots forget to close a filed plan after landing, and the search starts
// when they do. The number comes from admin -> Settings
// (flightLog.flightPlanPhone); tapping it dials, via a tel: link the phone
// turns into a native call prompt.

// The yellow-and-black "C" sign that marks the control (reporting) office
// on an airfield.
function ControlBadge() {
  return (
    <Box
      aria-hidden
      sx={{
        width: 52,
        height: 52,
        flexShrink: 0,
        bgcolor: '#f5d800',
        border: '3px solid #000',
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography sx={{ fontWeight: 900, fontSize: '2rem', color: '#000', lineHeight: 1 }}>C</Typography>
    </Box>
  )
}

// A wiggle now and then to draw the eye, pausing between shakes rather
// than looping continuously.
function ShakingPhoneIcon() {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        transformOrigin: '50% 70%',
        animation: 'flightPlanPhoneShake 3.5s ease-in-out infinite',
        '@keyframes flightPlanPhoneShake': {
          '0%, 8%': { transform: 'rotate(0deg)' },
          '1%': { transform: 'rotate(-16deg)' },
          '2%': { transform: 'rotate(13deg)' },
          '3%': { transform: 'rotate(-10deg)' },
          '4%': { transform: 'rotate(7deg)' },
          '5%': { transform: 'rotate(-4deg)' },
          '6%': { transform: 'rotate(2deg)' },
          '7%, 100%': { transform: 'rotate(0deg)' },
        },
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      <Icon icon="mdi:phone-alert-outline" />
    </Box>
  )
}

export function FlightPlanReminder({ phone }: { phone: string }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <ControlBadge />
        <Button
          fullWidth
          variant="outlined"
          color="warning"
          startIcon={<ShakingPhoneIcon />}
          onClick={() => setOpen(true)}
          // Orange text on the page background is too faint; the border and
          // icon keep the colour.
          sx={{ minHeight: 48, color: 'text.primary', '& .MuiButton-startIcon': { color: 'warning.main' } }}
        >
          {t('flightLog.flightPlanReminder')}
        </Button>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{t('flightLog.flightPlanReminderTitle')}</DialogTitle>
        <DialogContent>
          {phone ? (
            <Box sx={{ textAlign: 'center', py: 1 }}>
              <Link
                href={`tel:${phone.replace(/[\s()-]+/g, '')}`}
                underline="none"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, fontSize: '1.5rem', fontWeight: 700 }}
              >
                <Icon icon="mdi:phone" />
                {phone}
              </Link>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('flightLog.flightPlanNoNumber')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
